import { ArrowRight } from 'lucide-react';
import { amenityKind } from '@spacebooking/shared';
import type { PlanTable, WayfindingEntry } from '../lib/types';
import { classNames, metres } from '../lib/format';
import { Pictogram } from './pictograms';
import { routeKey } from './FloorMap';

/** Nearest sign of each kind, closest first. */
export function nearestByKind(entries: WayfindingEntry[]): WayfindingEntry[] {
  const best = new Map<string, WayfindingEntry>();
  for (const e of entries) {
    const key = `${e.kind}:${e.label}`;
    const current = best.get(key);
    if (!current || e.walk_m < current.walk_m) best.set(key, e);
  }
  return [...best.values()].sort((a, b) => a.walk_m - b.walk_m);
}

export function SignChip({ kind, size = 28 }: { kind: string; size?: number }) {
  const info = amenityKind(kind);
  return (
    <span className={classNames('sign-chip', `sign-${info?.category ?? 'facility'}`)} style={{ width: size, height: size }}>
      <Pictogram icon={info?.icon ?? 'info'} size={Math.round(size * 0.64)} strokeWidth={2.2} />
    </span>
  );
}

/**
 * "Toilets 9 m ↗" for each facility, with the arrow pointing the way on the
 * map. Hovering or tapping a row highlights its walking route on the map.
 */
export function WayfindingList(props: {
  table: PlanTable;
  active?: string | null;
  onActive?: (key: string | null) => void;
  limit?: number;
  compact?: boolean;
}) {
  const entries = nearestByKind(props.table.wayfinding).slice(0, props.limit ?? 8);
  if (entries.length === 0) return null;
  return (
    <ul className={classNames('wayfinding', props.compact && 'is-compact')}>
      {entries.map((e) => {
        const key = routeKey(e);
        // Direction of the first leg of the walk, in map terms (up = top of the plan).
        const next = e.path[1] ?? [e.x, e.y];
        const angle = (Math.atan2(next[1] - props.table.y, next[0] - props.table.x) * 180) / Math.PI;
        return (
          <li key={key}>
            <button
              type="button"
              className={classNames('wayfinding-row', props.active === key && 'is-active')}
              onMouseEnter={() => props.onActive?.(key)}
              onMouseLeave={() => props.onActive?.(null)}
              onFocus={() => props.onActive?.(key)}
              onBlur={() => props.onActive?.(null)}
              onClick={() => props.onActive?.(props.active === key ? null : key)}
            >
              <SignChip kind={e.kind} size={props.compact ? 24 : 28} />
              <span className="wayfinding-label">{e.label}</span>
              <span className="wayfinding-dist num">{metres(e.walk_m)}</span>
              <ArrowRight className="wayfinding-arrow" style={{ transform: `rotate(${angle}deg)` }} aria-hidden />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
